import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { completeAttempt, getAttempt, getQuestionsByIds } from "../lib/db";
import { isChoiceAnswerCorrect, isTextAnswerCorrect } from "../lib/grading";
import type { Attempt as AttemptType, AttemptAnswer, Question } from "../lib/types";
import { QuizTopBar } from "../components/QuizTopBar";
import { QuestionPhoto } from "../components/QuestionPhoto";
import "./Quiz.css";

export default function Attempt() {
  const { attemptId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState<AttemptType | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, AttemptAnswer>>({});
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!attemptId) return;
      try {
        const att = await getAttempt(attemptId);
        if (!alive) return;
        if (!att || att.userId !== user?.uid) {
          setError("No encontramos este test.");
          setLoading(false);
          return;
        }
        if (att.status === "completed") {
          navigate(`/tests/review/${attemptId}`, { replace: true });
          return;
        }
        const qs = await getQuestionsByIds(att.questionIds);
        // Preserve attempt order.
        const ordered = att.questionIds
          .map((id) => qs.find((q) => q.id === id))
          .filter((q): q is Question => !!q);
        if (!alive) return;
        setAttempt(att);
        setQuestions(ordered);
        setAnswers(att.answers ?? {});
      } catch (err) {
        console.error(err);
        if (alive) setError("No se pudo cargar el test.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [attemptId, user, navigate]);

  const current = questions[index];
  const total = questions.length;
  const isLast = index === total - 1;

  const usesImageChoices = useMemo(
    () => !!current?.choices?.some((c) => c.imageUrl),
    [current],
  );

  function selectChoice(choiceId: string) {
    if (!current) return;
    setAnswers((prev) => ({
      ...prev,
      [current.id]: {
        questionId: current.id,
        choiceId,
        correct: isChoiceAnswerCorrect(choiceId, current),
      },
    }));
  }

  function setText(text: string) {
    if (!current) return;
    setAnswers((prev) => ({
      ...prev,
      [current.id]: {
        questionId: current.id,
        text,
        correct: isTextAnswerCorrect(text, current.acceptedAnswers),
      },
    }));
  }

  async function finalize() {
    if (!attempt) return;
    setSubmitting(true);
    try {
      const score = Object.values(answers).filter((a) => a.correct).length;
      await completeAttempt(attempt.id, answers, score);
      navigate(`/tests/review/${attempt.id}`, { replace: true });
    } catch (err) {
      console.error(err);
      setError("No se pudo guardar el test.");
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="quiz"><div className="spinner" style={{ margin: "80px auto" }} /></div>;
  }
  if (error) {
    return (
      <div className="quiz quiz--message">
        <p>{error}</p>
        <button className="btn" onClick={() => navigate("/tests")}>
          Volver al inicio
        </button>
      </div>
    );
  }
  if (!attempt || !current) return null;

  const answer = answers[current.id];

  return (
    <div className="quiz">
      <QuizTopBar
        courseTitle={attempt.courseTitle}
        subtitle={`Intento`}
        index={index}
        total={total}
        rightLabel={`Pregunta nº ${index + 1}`}
        rightSub={`de ${total}`}
      />

      <div className="quiz__body">
        <p className="quiz__qlabel">Pregunta nº {index + 1}</p>
        <h2 className="quiz__qtext">{current.text}</h2>

        {current.imageUrl && <QuestionPhoto src={current.imageUrl} />}

        {current.type === "text" ? (
          <input
            className="quiz__input"
            type="text"
            placeholder="Escribe aquí..."
            value={answer?.text ?? ""}
            onChange={(e) => setText(e.target.value)}
          />
        ) : usesImageChoices ? (
          <div className="choice-grid">
            {current.choices?.map((c) => (
              <button
                key={c.id}
                className={
                  "choice-card" +
                  (answer?.choiceId === c.id ? " choice-card--selected" : "")
                }
                onClick={() => selectChoice(c.id)}
              >
                {c.imageUrl && <img src={c.imageUrl} alt={c.text} />}
                {c.text && <span>{c.text}</span>}
              </button>
            ))}
          </div>
        ) : (
          <div className="choice-list">
            {current.choices?.map((c) => (
              <button
                key={c.id}
                className={
                  "pill" + (answer?.choiceId === c.id ? " pill--selected" : "")
                }
                onClick={() => selectChoice(c.id)}
              >
                {c.text}
              </button>
            ))}
          </div>
        )}

        <div className="quiz__nav">
          <button
            className="btn btn--pill"
            disabled={index === 0}
            style={index === 0 ? { background: "var(--muted-btn)" } : undefined}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
          >
            Anterior
          </button>
          {isLast ? (
            <button className="btn btn--pill" disabled={submitting} onClick={finalize}>
              {submitting ? "Guardando…" : "Finalizar"}
            </button>
          ) : (
            <button
              className="btn btn--pill"
              onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
            >
              Siguiente
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
